import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { PortalEnrolleeDetail, PortalMilestone } from '../api'
import { getEnrollee, attestMilestone, dismissMilestone, confirmMilestone } from '../api'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function groupByPhase(milestones: PortalMilestone[]): { label: string; items: PortalMilestone[] }[] {
  const phases: Map<string, PortalMilestone[]> = new Map()
  for (const m of milestones) {
    const key = m.phase_role ?? m.phase_type ?? 'Your Journey'
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    if (!phases.has(label)) phases.set(label, [])
    phases.get(label)!.push(m)
  }
  return Array.from(phases.entries()).map(([label, items]) => ({ label, items }))
}

// A milestone is "actionable" if it has an action the user can take right now.
// Inactive future milestones (not_started with no target_at) are shown compactly.
function isActionable(m: PortalMilestone): boolean {
  const es = m.effective_state
  return es === 'overdue' || es === 'proposed' ||
    (es === 'not_started' && m.target_at !== null)
}

// ── Milestone card ────────────────────────────────────────────────────────────

type MilestoneCardState = 'idle' | 'busy' | 'done' | 'dismissed'

function MilestoneCard({
  milestone,
  onAction,
}: {
  milestone: PortalMilestone
  onAction: (id: string, action: 'attest' | 'dismiss' | 'confirm') => Promise<void>
}) {
  const [cardState, setCardState] = useState<MilestoneCardState>('idle')

  const state = milestone.effective_state

  async function act(action: 'attest' | 'dismiss' | 'confirm') {
    setCardState('busy')
    try {
      await onAction(milestone.id, action)
      setCardState(action === 'dismiss' ? 'dismissed' : 'done')
    } catch {
      setCardState('idle')
    }
  }

  // Complete — show faded with celebration if any
  if (state === 'complete' || cardState === 'done') {
    return (
      <div className="p-mc p-mc-done">
        <div className="p-mc-dot p-mc-dot-done">✓</div>
        <div className="p-mc-body">
          <div className="p-mc-name p-mc-name-done">{milestone.name}</div>
          <div className="p-mc-meta">
            {milestone.completed_at ? `Completed ${formatDate(milestone.completed_at)}` : 'Complete'}
          </div>
          {milestone.celebration_message && (
            <div className="p-mc-celebration">{milestone.celebration_message}</div>
          )}
        </div>
      </div>
    )
  }

  // Dismissed
  if (cardState === 'dismissed') {
    return (
      <div className="p-mc p-mc-dismissed">
        <div className="p-mc-dot p-mc-dot-inactive">·</div>
        <div className="p-mc-body">
          <div className="p-mc-name p-muted">{milestone.name}</div>
          <div className="p-mc-meta">Dismissed</div>
        </div>
      </div>
    )
  }

  // Inactive future — compact, no action
  if (!isActionable(milestone)) {
    return (
      <div className="p-mc p-mc-inactive">
        <div className="p-mc-dot p-mc-dot-inactive">·</div>
        <div className="p-mc-body">
          <div className="p-mc-name p-mc-name-inactive">{milestone.name}</div>
          {milestone.target_at && (
            <div className="p-mc-meta">Due {formatDate(milestone.target_at)}</div>
          )}
        </div>
      </div>
    )
  }

  // Actionable states
  const dotClass =
    state === 'overdue' ? 'p-mc-dot p-mc-dot-overdue' :
    state === 'proposed' ? 'p-mc-dot p-mc-dot-proposed' :
    'p-mc-dot p-mc-dot-active'

  const dotLabel = state === 'overdue' ? '!' : state === 'proposed' ? '?' : '→'

  const metaText =
    state === 'overdue' && milestone.target_at ? `Overdue — was due ${formatDate(milestone.target_at)}` :
    state === 'proposed' && milestone.proposed_at ? `Suggested ${formatDate(milestone.proposed_at)}` :
    state === 'proposed' ? 'Suggested by your coach' :
    milestone.target_at ? `Due ${formatDate(milestone.target_at)}` : ''

  return (
    <div className={`p-mc${state === 'overdue' ? ' p-mc-overdue' : ''}${state === 'proposed' ? ' p-mc-proposed' : ''}`}>
      <div className={dotClass}>{dotLabel}</div>
      <div className="p-mc-body">
        <div className="p-mc-name">{milestone.name}</div>
        {metaText && (
          <div className={`p-mc-meta${state === 'overdue' ? ' p-mc-meta-overdue' : ''}`}>{metaText}</div>
        )}
        {milestone.user_facing_description && (
          <p className="p-mc-desc">{milestone.user_facing_description}</p>
        )}
        <div className="p-mc-actions">
          {(state === 'not_started' || state === 'overdue') && milestone.can_mark_done === false && (
            milestone.action_url ? (
              <>
                <a className="p-btn p-btn-primary p-btn-sm" href={milestone.action_url} target="_blank" rel="noopener noreferrer">
                  {milestone.action_label ?? 'Open'}
                </a>
                <div className="p-mc-meta">This ticks itself off once you have done it.</div>
              </>
            ) : (
              <div className="p-mc-meta">This ticks itself off when it happens.</div>
            )
          )}
          {(state === 'not_started' || state === 'overdue') && milestone.can_mark_done !== false && (
            <>
              <button
                className="p-btn p-btn-primary p-btn-sm"
                disabled={cardState === 'busy'}
                onClick={() => act('attest')}
              >
                {cardState === 'busy' ? '…' : 'Mark complete'}
              </button>
              <button
                className="p-btn p-btn-ghost p-btn-sm"
                disabled={cardState === 'busy'}
                onClick={() => act('dismiss')}
              >
                Dismiss
              </button>
            </>
          )}
          {state === 'proposed' && (
            <>
              <button
                className="p-btn p-btn-primary p-btn-sm"
                disabled={cardState === 'busy'}
                onClick={() => act('confirm')}
              >
                {cardState === 'busy' ? '…' : 'Accept'}
              </button>
              <button
                className="p-btn p-btn-outline p-btn-sm"
                disabled={cardState === 'busy'}
                onClick={() => act('dismiss')}
              >
                Not now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Roadmap ───────────────────────────────────────────────────────────────────

export default function Roadmap() {
  const { enrolleeId } = useParams<{ enrolleeId: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<PortalEnrolleeDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  async function load() {
    if (!enrolleeId) return
    setLoading(true)
    setErr('')
    try {
      const d = await getEnrollee(enrolleeId)
      setData(d)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Failed to load roadmap.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [enrolleeId])

  async function handleAction(id: string, action: 'attest' | 'dismiss' | 'confirm') {
    if (action === 'attest') await attestMilestone(id)
    else if (action === 'dismiss') await dismissMilestone(id)
    else await confirmMilestone(id)
    await load()
  }

  if (loading) {
    return (
      <div className="p-page">
        <button className="p-roadmap-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
        <div className="p-center" style={{ minHeight: 200 }}>
          <div className="p-spinner" />
        </div>
      </div>
    )
  }

  if (err || !data) {
    return (
      <div className="p-page">
        <button className="p-roadmap-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
        <div className="p-error">{err || 'Roadmap not found.'}</div>
      </div>
    )
  }

  const { enrollee, milestones } = data
  const programName = enrollee.program_label_override ?? enrollee.program_name
  const complete = milestones.filter(m => m.state === 'complete').length
  const total = milestones.length
  const overdue = milestones.filter(m => m.effective_state === 'overdue').length
  const pct = total ? Math.round((complete / total) * 100) : 0
  const phases = groupByPhase(milestones)

  return (
    <div className="p-page">
      <button className="p-roadmap-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>

      {/* Hero summary card */}
      <div className="p-roadmap-hero">
        <div className="p-roadmap-program">{programName}</div>
        <div className="p-roadmap-stats">
          <span className="p-badge p-badge-active">{enrollee.state.toUpperCase()}</span>
          <span className="p-roadmap-stat">Day <strong>{enrollee.day_of_arc_offset_days ?? 0}</strong></span>
          <span className="p-roadmap-stat"><strong>{complete}</strong> of {total} milestones complete</span>
          {overdue > 0 && (
            <span className="p-roadmap-stat" style={{ color: 'var(--red)' }}>
              <strong>{overdue}</strong> overdue
            </span>
          )}
        </div>
        <div className="p-progress-row">
          <div className="p-progress-bar-wrap">
            <div className="p-progress-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="p-progress-label">{pct}%</div>
        </div>
      </div>

      {/* Phase groups with milestone cards */}
      {phases.map(phase => (
        <div key={phase.label} className="p-phase">
          <div className="p-phase-label">{phase.label}</div>
          <div className="p-milestone-cards">
            {phase.items.map(m => (
              <MilestoneCard
                key={m.id}
                milestone={m}
                onAction={handleAction}
              />
            ))}
          </div>
        </div>
      ))}

      {milestones.length === 0 && (
        <div className="p-empty">No milestones set yet. Check back soon.</div>
      )}
    </div>
  )
}
